/**
 * Boolean role-bucket helpers. Each one mirrors a helper in
 * `firestore.rules` so the three security layers agree:
 *
 *   1. UI gate (web + mobile)  ← these functions
 *   2. Cloud Function caller check (server-enforced)
 *   3. Firestore rule helper (last line of defense)
 *
 * Calling these from UI code keeps inline `role === 'admin'
 * || role === 'superAdmin' || ...` chains out of feature files.
 * If the role buckets change, edit them here — every consumer
 * gets the update.
 */
import type { RoleKey } from './roles';

/** "Admin-ish" — Super Admin OR Admin. The studio's owners.
 *  Used for things like project delete, studio-profile edit. */
export function isAdminish(role: RoleKey | null | undefined): boolean {
  return role === 'superAdmin' || role === 'admin';
}

/** Can manage projects — create / edit / change status / change
 *  phase / approve material requests. SA, Admin, Manager. */
export function canManageProjects(role: RoleKey | null | undefined): boolean {
  return role === 'superAdmin' || role === 'admin' || role === 'manager';
}

/** Can read + write finance data — transactions, payouts, org
 *  finances. SA, Admin, Manager, Accountant. */
export function canFinance(role: RoleKey | null | undefined): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'accountant'
  );
}

/** Can approve a material-request submission. Same set as
 *  `canManageProjects` — the approval is a project-level action. */
export function canApproveMaterial(role: RoleKey | null | undefined): boolean {
  return canManageProjects(role);
}

/** Can write CRM data — leads, appointments. SA, Admin, Manager. */
export function canCRMWrite(role: RoleKey | null | undefined): boolean {
  return canManageProjects(role);
}

/** Can edit tasks (create / update / delete). Wider than
 *  `canManageProjects` because Site Engineer + Designer do the
 *  task work itself. */
export function canEditTasks(role: RoleKey | null | undefined): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'siteEngineer' ||
    role === 'designer'
  );
}

/** Can write party data. Wider than CRM because site staff
 *  create vendor / contractor parties inline from the expense
 *  form. */
export function canPartyWrite(role: RoleKey | null | undefined): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'accountant' ||
    role === 'siteEngineer' ||
    role === 'supervisor'
  );
}

/** Can change someone else's role / invite / remove members.
 *  Server-side `setMemberRole` + `inviteMember` + `removeMember`
 *  callables also enforce this, with extra peer-protection on
 *  top (Manager can't touch another Manager). */
export function canManageTeam(role: RoleKey | null | undefined): boolean {
  return canManageProjects(role);
}

/** Can approve a pending transaction. Same set as finance —
 *  Accountant is included because they manage money daily even
 *  though they don't manage projects. */
export function canApproveTransaction(
  role: RoleKey | null | undefined,
): boolean {
  return canFinance(role);
}

/** Can mark attendance. Wider than `canManageProjects` because
 *  site staff (Engineer + Supervisor) do the actual attendance
 *  ticking on-site. Mirrors `firestore.rules:canMarkAttendance`. */
export function canMarkAttendance(role: RoleKey | null | undefined): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'siteEngineer' ||
    role === 'supervisor'
  );
}

/** Can submit a Daily Progress Report. Broadest write-ish gate
 *  in the matrix — every project-member role EXCEPT client and
 *  accountant. Mirrors `firestore.rules:canWriteDpr`. */
export function canWriteDpr(role: RoleKey | null | undefined): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'siteEngineer' ||
    role === 'supervisor' ||
    role === 'viewer'
  );
}

/** Can read project transactions org-wide (within projects they
 *  can see). Submit-only roles (Site Engineer / Supervisor) get a
 *  per-creator restriction layered on top by the rule, and Client
 *  is excluded entirely. Mirrors
 *  `firestore.rules:canReadProjectTransactions`. */
export function canReadProjectTransactions(
  role: RoleKey | null | undefined,
): boolean {
  return (
    role === 'superAdmin' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'accountant' ||
    role === 'viewer'
  );
}

/** "Submit-only" transaction role — can file `payment_out` rows
 *  that land in `pending_approval`, can read ONLY their own
 *  submissions (Firestore rule enforces per-creator scope). Used by
 *  hooks to know they MUST add `where('createdBy', '==', uid)` to
 *  their transaction queries — without it, the rule rejects the
 *  unscoped query and the list comes back empty for the role.
 *
 *  Mirrors the mobile `can('transaction.submit') && !can('transaction.write')`
 *  pattern used in `src/features/transactions/useTransactions.ts`. */
export function isSubmitOnlyTxnRole(
  role: RoleKey | null | undefined,
): boolean {
  return role === 'siteEngineer' || role === 'supervisor';
}

/** Caller has at least SOME transaction read visibility — either
 *  org-wide via `canReadProjectTransactions`, or own-only via
 *  `isSubmitOnlyTxnRole`, or per-project as a Client. Returns false
 *  for roles with ZERO transaction access (Designer today). Used by
 *  hooks to skip attaching a transactions listener entirely for
 *  roles the Firestore rule rejects in full — avoids permission-
 *  denied snapshot errors + the `b815/ca9` SDK assertion. */
export function canSeeAnyTransactions(role: RoleKey | null | undefined): boolean {
  return (
    canReadProjectTransactions(role)
    || isSubmitOnlyTxnRole(role)
    || role === 'client'  // Client sees their own project's txns via the rule
  );
}
